import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { supabase } from './supabaseClient';
import { useSession } from './SessionContext';
import AircraftTechLog from './AircraftTechLog';
import { defaultAppSettings, loadAppSettings } from './appSettings';
import { briefingStatusMeta, getBriefingRecordForBooking, getBriefingStatus } from './briefingPlanner';
import { fetchOfficialTechLogsForBookings, indexOfficialTechLogs, saveOfficialTechLogEntry } from './officialTechLog';
import { getResolvedPostFlightStatus } from './booking/activeFlightSelection';
import {
  calculatePaymentTotals,
} from './admin/paymentUtils';
import {
  fetchOrganisationBaseAirfieldIcao,
  resolveBaseAirfield,
} from './booking/baseAirfieldResolution';
import { bookingUpdateContainsId } from './booking/bookingUtils';
import { getReadOrganisationId } from './booking/bookingService';
import './pilotPagesMobile.css';

const buttonStyle = {
  background: '#1ecfff',
  color: '#181a2a',
  border: 'none',
  borderRadius: 8,
  padding: '0.5rem 1.2rem',
  fontSize: '1rem',
  fontWeight: 600,
  cursor: 'pointer',
  boxShadow: '0 2px 8px rgba(30,207,255,0.10)'
};

const pageStyle = {
  minHeight: '100vh',
  background: 'linear-gradient(180deg, #081626 0%, #10172a 48%, #15182b 100%)',
  color: 'white',
  padding: '1.25rem',
};

const shellStyle = {
  width: '100%',
  maxWidth: 980,
  margin: '0 auto',
};

const heroStyle = {
  background: 'rgba(24, 26, 42, 0.92)',
  border: '1px solid rgba(182,234,255,0.16)',
  borderRadius: 18,
  padding: '1rem 1.25rem',
  marginBottom: '1rem',
  boxShadow: '0 10px 26px rgba(0,0,0,0.18)',
};

const cardStyle = {
  background: 'rgba(24, 26, 42, 0.92)',
  border: '1px solid rgba(182,234,255,0.16)',
  borderRadius: 20,
  padding: '1.15rem',
  boxShadow: '0 14px 34px rgba(0,0,0,0.22)',
};

const mutedTextStyle = {
  color: '#9fb7d7',
  lineHeight: 1.45,
};

function normalizeRole(...candidates) {
  for (const candidate of candidates) {
    const value = String(candidate || '').trim().toLowerCase();
    if (value) return value;
  }
  return '';
}

function collectRoles(...candidates) {
  return candidates
    .map(candidate => String(candidate || '').trim().toLowerCase())
    .filter(Boolean);
}

function isOwnerByEmail(email) {
  const normalized = String(email || '').trim().toLowerCase();
  return normalized === 'owner@demo.wingit.app';
}

function sanitizeTechLogPrefill(entry = {}) {
  const source = entry && typeof entry === 'object' ? entry : {};
  const { departureTime, arrivalTime, ...rest } = source;
  return rest;
}

function formatLocalDate(date) {
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
}

function formatLocalTime(date) {
  const h = String(date.getHours()).padStart(2, '0');
  const m = String(date.getMinutes()).padStart(2, '0');
  return `${h}:${m}`;
}

function formatDateTimeLabel(date) {
  return `${formatLocalDate(date)} ${formatLocalTime(date)}`;
}



function addMinutes(date, minutes) {
  return new Date(date.getTime() + Number(minutes || 0) * 60000);
}

function parseDateTime(dateValue, timeValue) {
  if (!dateValue || !timeValue) return null;
  const parsed = new Date(`${dateValue}T${timeValue}`);
  return Number.isFinite(parsed.getTime()) ? parsed : null;
}

function getBookingStartDate(booking) {
  return parseDateTime(booking.date, booking.time);
}

function getBookingEndDate(booking) {
  const explicitEnd = parseDateTime(booking.end_date, booking.end_time);
  if (explicitEnd) return explicitEnd;

  const start = getBookingStartDate(booking);
  if (!start) return null;
  return addMinutes(start, Number(booking.duration) || 60);
}

function formatBookingRange(booking) {
  const start = getBookingStartDate(booking);
  const end = getBookingEndDate(booking);
  if (!start || !end) return `${booking.date || ''} ${booking.time || ''}`.trim();
  if (formatLocalDate(start) === formatLocalDate(end)) {
    return `${formatLocalDate(start)} ${formatLocalTime(start)}–${formatLocalTime(end)}`;
  }
  return `${formatDateTimeLabel(start)} → ${formatDateTimeLabel(end)}`;
}


  



 

  

function isMultiDayBooking(booking) {
  const start = getBookingStartDate(booking);
  const end = getBookingEndDate(booking);
  if (!start || !end) return false;
  return formatLocalDate(start) !== formatLocalDate(end);
}

function getReturnLegStatusMeta(booking) {
  if (!isMultiDayBooking(booking)) return null;

  const record = booking?.briefing_data?.return_leg_preflight;
  if (record?.completed) {
    return {
      label: 'COMPLETE',
      fill: '#7df0a8',
      border: '#9af5bb',
      background: 'rgba(125, 240, 168, 0.12)',
    };
  }

  if (record?.status === 'pending') {
    return {
      label: 'PENDING',
      fill: '#ffcc66',
      border: '#ffd98c',
      background: 'rgba(255, 204, 102, 0.12)',
    };
  }

  return {
    label: 'REQUIRED',
    fill: '#ff9c9c',
    border: '#ff9393',
    background: 'rgba(255, 110, 110, 0.12)',
  };
}

function formatStatusUpdatedAt(value) {
  const parsed = new Date(value || '');
  if (!Number.isFinite(parsed.getTime())) return '';
  return parsed.toLocaleString(undefined, {
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  });
}

function isMissingEndColumnsError(error) {
  const message = String(error?.message || '').toLowerCase();
  return message.includes('end_date') || message.includes('end_time') || message.includes('column');
}

function isMissingAircraftMethodColumnError(error) {
  const message = String(error?.message || '').toLowerCase();
  return message.includes('recording_method') || message.includes('column');
}

function isMissingClosureColumnsError(error) {
  const message = String(error?.message || '').toLowerCase();
  return message.includes('tech_log_status')
    || message.includes('payment_status')
    || message.includes('payment_amount')
    || message.includes('payment_completed_at')
    || message.includes('tech_log_completed_at')
    || message.includes('column');
}

function isBookingsTimeOrderError(error) {
  const code = String(error?.code || '').toUpperCase();
  const message = String(error?.message || '').toLowerCase();
  return code === 'PGRST100'
    || message.includes('failed to parse order')
    || (message.includes('order') && message.includes('time'));
}

async function runBookingsQueryWithTimeOrderFallback(buildQuery) {
  let result = await buildQuery(true);
  if (!result.error || !isBookingsTimeOrderError(result.error)) {
    return result;
  }

  result = await buildQuery(false);
  return result;
}

function getTechLogStatus(booking, techLogEntry) {
  const resolvedStatus = getResolvedPostFlightStatus(booking);

  if (resolvedStatus === 'did_not_fly') return 'did_not_fly';
  if (resolvedStatus === 'completed') return 'completed';
  if (String(booking?.tech_log_status || '').toLowerCase() === 'defect_reported') return 'defect_reported';

  const hasSavedTechLog = techLogEntry && typeof techLogEntry === 'object' && Object.keys(techLogEntry).length > 0;
  return hasSavedTechLog ? 'completed' : 'open';
}

function getPaymentStatus(booking) {
  const status = String(booking?.payment_status || '').toLowerCase();
  if (status === 'paid') return 'paid';
  if (status === 'not_required') return 'not_required';
  return 'outstanding';
}

export function isMemberStripePaymentEligible({
  booking,
  userId,
  paymentStatus,
  total,
  paymentEnabled,
  stripeConnected,
  now = new Date(),
}) {
  const end = getBookingEndDate(booking);

  return Boolean(
    booking?.user_id === userId
    && paymentEnabled
    && end
    && end <= now
    && paymentStatus === 'outstanding'
    && Number(total) > 0
    && stripeConnected
  );
}

export function buildMemberStripeCheckoutRequest({
  booking,
  customerEmail,
  origin,
}) {
  return {
    bookingId: booking.id,
    currency: 'gbp',
    customerEmail: customerEmail || undefined,
    description: `Flight booking ${booking.id}`,
    successUrl: `${origin}/my-bookings?stripe=success`,
    cancelUrl: `${origin}/my-bookings?stripe=cancel`,
  };
}



 

  

function getSmallStatusPill(label, status) {
  if (status === 'good') {
    return {
      label,
      fill: '#7df0a8',
      border: '#9af5bb',
      background: 'rgba(125, 240, 168, 0.12)',
    };
  }

  if (status === 'neutral') {
    return {
      label,
      fill: '#9fdcff',
      border: '#4d79a8',
      background: 'rgba(77, 121, 168, 0.16)',
    };
  }

  return {
    label,
    fill: '#ffcc66',
    border: '#ffd98c',
    background: 'rgba(255, 204, 102, 0.12)',
  };
}

function StatusPill({ meta, label }) {
  if (!meta) return null;

  return (
    <span style={{
      display: 'inline-flex',
      alignItems: 'center',
      gap: 6,
      padding: '0.38rem 0.7rem',
      borderRadius: 999,
      border: `1px solid ${meta.border}`,
      background: meta.background,
      color: meta.fill,
      fontWeight: 850,
      letterSpacing: '0.035em',
      fontSize: '0.76rem',
      lineHeight: 1.1,
      whiteSpace: 'nowrap',
    }}>
      {label || meta.label}
    </span>
  );
}

export default function MyBookings() {
  const navigate = useNavigate();
  const session = useSession();
  const user = session?.user;
  const organisationId =
    session?.profile?.organisation_id ||
    user?.organisation_id ||
    user?.user_metadata?.organisation_id ||
    user?.app_metadata?.organisation_id ||
    '';
  const [resolvedRole, setResolvedRole] = useState(normalizeRole(user?.user_metadata?.role, user?.app_metadata?.role, user?.role));
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [aircraftMap, setAircraftMap] = useState({});
  const [expandedBookingId] = useState(null);
  const [techLogMap, setTechLogMap] = useState({});
  const [techLogVersionMap, setTechLogVersionMap] = useState({});
  const [saveNoticeMap, setSaveNoticeMap] = useState({});
  const [checkoutLoadingMap, setCheckoutLoadingMap] = useState({});
  const [paymentErrorMap, setPaymentErrorMap] = useState({});
  const [stripeBanner, setStripeBanner] = useState('');
  const [, setBriefingTick] = useState(0);
  const appSettings = useMemo(
    () =>
      organisationId
        ? loadAppSettings(organisationId)
        : defaultAppSettings,
    [organisationId]
  );
  const settingsBaseAirfield = String(appSettings?.operational?.baseAirfield || '').trim().toUpperCase();
  const [databaseBaseAirfield, setDatabaseBaseAirfield] = useState('');

  useEffect(() => {
    let cancelled = false;

    async function loadClubBaseAirfield() {
      const icao = await fetchOrganisationBaseAirfieldIcao(
        supabase,
        organisationId
      );

      if (!cancelled) {
        setDatabaseBaseAirfield(icao);
      }
    }

    loadClubBaseAirfield();

    return () => {
      cancelled = true;
    };
  }, [organisationId]);

  const baseAirfield = resolveBaseAirfield(
    databaseBaseAirfield,
    settingsBaseAirfield
  );
  const apiBase = String(process.env.REACT_APP_API_BASE_URL || '').trim().replace(/\/+$/, '');

  useEffect(() => {
    let cancelled = false;

    async function resolveRole() {
      if (!user?.id) {
        if (!cancelled) setResolvedRole('');
        return;
      }

      const { data } = await supabase
        .from('users')
        .select('role')
        .eq('id', user.id)
        .maybeSingle();

      if (!cancelled) {
        setResolvedRole(normalizeRole(
          data?.role,
          user?.user_metadata?.role,
          user?.app_metadata?.role,
          user?.role
        ));
      }
    }

    resolveRole();
    return () => {
      cancelled = true;
    };
  }, [user?.id, user?.user_metadata?.role, user?.app_metadata?.role, user?.role]);

  const roleCandidates = collectRoles(
    resolvedRole,
    user?.user_metadata?.role,
    user?.app_metadata?.role,
    user?.role
  );
  const isOwner = roleCandidates.includes('owner') || isOwnerByEmail(user?.email);

  async function fetchAircraft() {
  const organisationId = await getReadOrganisationId(supabase);

  if (!organisationId) {
    setAircraftMap({});
    return;
  }

  let { data, error } = await supabase
    .from('aircraft')
    .select('id, registration, icao_type, recording_method')
    .eq('organisation_id', organisationId);

  if (error && isMissingAircraftMethodColumnError(error)) {
    const fallback = await supabase
      .from('aircraft')
      .select('id, registration, icao_type')
      .eq('organisation_id', organisationId);

    data = (fallback.data || []).map((row) => ({
      ...row,
      recording_method: appSettings?.techLog?.recordingMethod || 'hobbs',
    }));

    error = fallback.error;
  }

  if (!error && data) {
    const map = {};

    data.forEach((aircraft) => {
      map[aircraft.id] = aircraft;
    });

    setAircraftMap(map);
  }
}

  async function fetchBookings() {
    setLoading(true);
    setError('');
    if (!user) {
      setBookings([]);
      setLoading(false);
      return;
    }

    let { data, error } = await runBookingsQueryWithTimeOrderFallback((includeTimeOrder) => {
      let query = supabase
        .from('bookings')
        .select('id, aircraft_id, date, time, end_date, end_time, duration, briefing_status, briefing_data, briefing_updated_at, tech_log_status, tech_log_completed_at, tech_log_notes, payment_status, payment_amount, payment_completed_at, payment_notes')
        .eq('user_id', user.id)
        .order('date', { ascending: true });

      if (includeTimeOrder) {
        query = query.order('time', { ascending: true });
      }

      return query;
    });

    if (error && (isMissingEndColumnsError(error) || isMissingClosureColumnsError(error))) {
      const fallback = await runBookingsQueryWithTimeOrderFallback((includeTimeOrder) => {
        let query = supabase
          .from('bookings')
          .select('id, aircraft_id, date, time, duration, briefing_status, briefing_data, briefing_updated_at')
          .eq('user_id', user.id)
          .order('date', { ascending: true });

        if (includeTimeOrder) {
          query = query.order('time', { ascending: true });
        }

        return query;
      });

      data = (fallback.data || []).map((row) => ({
        ...row,
        end_date: null,
        end_time: null,
        tech_log_status: 'open',
        tech_log_completed_at: null,
        tech_log_notes: null,
        payment_status: 'outstanding',
        payment_amount: null,
        payment_completed_at: null,
        payment_notes: null,
      }));
      error = fallback.error;
    }

    if (error) {
      setError('Could not load bookings.');
      setLoading(false);
      return;
    }

    setBookings(data || []);
    setLoading(false);
  }

  useEffect(() => {
    fetchAircraft();
    fetchBookings();
    // eslint-disable-next-line
  }, [user]);

  useEffect(() => {
    let cancelled = false;

    async function fetchOfficialLogs() {
      if (!user?.id || bookings.length === 0) {
        setTechLogMap({});
        setTechLogVersionMap({});
        return;
      }

      const bookingIds = bookings.map((booking) => String(booking.id));
      const { data, error } = await fetchOfficialTechLogsForBookings(bookingIds);
      if (cancelled || error) return;

      const indexed = indexOfficialTechLogs(data || []);
      setTechLogMap(indexed.logMap);
      setTechLogVersionMap(indexed.versionMap);
    }

    fetchOfficialLogs();
    return () => {
      cancelled = true;
    };
  }, [user?.id, bookings]);

  useEffect(() => {
    const url = new URL(window.location.href);
    const stripe = url.searchParams.get('stripe');
    if (!stripe) return;

    if (stripe === 'success') {
      setStripeBanner('Payment successful. Stripe confirmed your checkout.');
      fetchBookings();
    } else if (stripe === 'cancel') {
      setStripeBanner('Payment cancelled. No charge was made.');
    }

    url.searchParams.delete('stripe');
    window.history.replaceState({}, '', url.toString());
    // fetchBookings is the stable local refresh used for the Stripe return path.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    function refreshBriefings() {
      setBriefingTick(tick => tick + 1);
    }

    window.addEventListener('storage', refreshBriefings);
    window.addEventListener('wingit-briefing-store-updated', refreshBriefings);
    return () => {
      window.removeEventListener('storage', refreshBriefings);
      window.removeEventListener('wingit-briefing-store-updated', refreshBriefings);
    };
  }, []);

  function updateBookingLocal(bookingId, updates) {
    setBookings(prev => prev.map(booking => (
      String(booking.id) === String(bookingId)
        ? { ...booking, ...updates }
        : booking
    )));
  }

  async function updateBookingClosureFields(bookingId, updates) {
    const { data: closureUpdateRows, error: updateError } = await supabase
      .from('bookings')
      .update(updates)
      .eq('id', bookingId)
      .select('id');

    if (updateError || !bookingUpdateContainsId(closureUpdateRows, bookingId)) {
      setSaveNoticeMap(prev => ({
        ...prev,
        [`${bookingId}:closure`]: updateError
          ? `Could not update closure status: ${updateError.message}`
          : 'Could not update closure status: booking record could not be confirmed as updated.',
      }));
      return false;
    }

    updateBookingLocal(bookingId, updates);
    return true;
  }

  async function handleTechLogSave(bookingId, entry) {
    const noticeKey = `${bookingId}:tech`;

    const bookingKey = String(bookingId);
        const savedAt = new Date().toISOString();
    const legs = Array.isArray(entry?.legs) ? entry.legs : [];
    const firstLeg = legs[0] || {};
    const lastLeg = legs[legs.length - 1] || {};

    const legFlightTimeTotal = legs.reduce((sum, leg) => {
      const value = Number(leg.flightTime);
      return sum + (Number.isFinite(value) && value > 0 ? value : 0);
    }, 0);

    const flightTime = Number(entry?.flightTime);
    const taxiTime = Number(entry?.taxiTime);

    const resolvedFlightTime = Number.isFinite(flightTime) && flightTime > 0
      ? flightTime
      : Number(legFlightTimeTotal.toFixed(2));

    const resolvedTaxiTime = Number.isFinite(taxiTime) && taxiTime > 0 ? taxiTime : 0;
    const resolvedTotalTime = Number((resolvedFlightTime + resolvedTaxiTime).toFixed(2));

    const entryWithPilotLogbook = {
      ...entry,
      pilotLogSavedAt: savedAt,
      pilotLogbook: {
        ...(entry?.pilotLogbook || {}),
        pilot: entry?.pilotName || user?.user_metadata?.full_name || user?.email || '',
        from: firstLeg.from || entry?.departureAirfield || '',
        to: lastLeg.to || entry?.arrivalAirfield || '',
        takeoffTime: firstLeg.takeoffTime || entry?.departureTime || '',
        landingTime: lastLeg.landingTime || entry?.arrivalTime || '',
        flightTime: resolvedFlightTime,
        taxiTime: resolvedTaxiTime,
        totalTime: resolvedTotalTime,
                seDayPic: resolvedFlightTime ? String(resolvedFlightTime) : '',
        seNightPic: entry?.nightTime || entry?.seNightPic || entry?.pilotLogbook?.seNightPic || '',
        instrumentFlight: entry?.instrumentFlight || entry?.pilotLogbook?.instrumentFlight || '',
notes: entry?.remarks || entry?.pilotLogbook?.notes || '',
        savedAt,
      },
    };
    const result = await saveOfficialTechLogEntry({
      bookingId: bookingKey,
      userId: user?.id,
      entry: entryWithPilotLogbook,
      expectedVersion: techLogVersionMap[bookingKey] ?? 0,
    });

    if (result.ok) {
      setTechLogMap(prev => ({ ...prev, [bookingKey]: entryWithPilotLogbook }));
      setTechLogVersionMap(prev => ({ ...prev, [bookingKey]: result.version }));

      await updateBookingClosureFields(bookingId, {
        tech_log_status: 'completed',
        tech_log_completed_at: new Date().toISOString(),
      });
    }

    setSaveNoticeMap(prev => ({
      ...prev,
      [noticeKey]: result.ok
        ? `Tech log saved at ${formatLocalTime(new Date())}. Pilot logbook will be generated from this Tech Log.`
        : (result.message || 'Another user is updating this tech log. Refresh and try again.'),
    }));

    window.setTimeout(() => {
      setSaveNoticeMap(prev => {
        if (!prev[noticeKey]) return prev;
        const next = { ...prev };
        delete next[noticeKey];
        return next;
      });
    }, result.ok ? 3000 : 4000);
  }

  function getPaymentTotals(booking) {
    return calculatePaymentTotals({
      booking,
      techLogEntry: techLogMap[booking.id] || {},
      billingBasis: appSettings.payment.billingBasis,
      hourlyRate: appSettings.payment.hourlyRate,
      landingFeeEnabled: appSettings.payment.landingFeeEnabled,
      landingFee: appSettings.payment.landingFee,
    });
  }

  async function handleStripeCheckout(booking, totals) {
    const bookingId = booking.id;
    setError('');
    setPaymentErrorMap(prev => ({ ...prev, [bookingId]: '' }));

    if (!Number.isFinite(totals.total) || totals.total <= 0) {
      setPaymentErrorMap(prev => ({
        ...prev,
        [bookingId]: 'Payment total is 0. Enter flight/tech time or update billing settings before paying.'
      }));
      return;
    }

    setCheckoutLoadingMap(prev => ({ ...prev, [bookingId]: true }));

    try {
      const authToken = session?.access_token;
      if (!authToken) {
        throw new Error('Session expired. Please sign in again.');
      }

      const response = await fetch(`${apiBase}/api/billing/stripe/checkout-session`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${authToken}`,
        },
        body: JSON.stringify(buildMemberStripeCheckoutRequest({
          booking,
          customerEmail: user?.email,
          origin: window.location.origin,
        })),
      });

      let payload = {};

      try {
        payload = await response.json();
      } catch (parseErr) {
        payload = {};
      }

      if (!response.ok) {
        throw new Error(payload?.error || payload?.detail || 'Unable to start Stripe checkout.');
      }

      if (!payload?.url) {
        throw new Error('Stripe session URL not returned by server.');
      }

      window.location.assign(payload.url);
    } catch (err) {
      setPaymentErrorMap(prev => ({ ...prev, [bookingId]: err.message || 'Stripe checkout failed.' }));
      setCheckoutLoadingMap(prev => ({ ...prev, [bookingId]: false }));
    }
  }
  const postFlightBookings = bookings.filter((b) => {
    const bookingTechEntry = techLogMap[String(b.id)] || {};

    const techStatus = getTechLogStatus(b, bookingTechEntry);
    const paymentStatus = getPaymentStatus(b);

    const techComplete = techStatus === 'completed' || techStatus === 'did_not_fly';
    const paymentComplete =
      paymentStatus === 'paid' ||
      paymentStatus === 'not_required';

    return !(techComplete && paymentComplete);
  });
  return (
    <div className="pilot-page pilot-my-bookings" style={pageStyle}>
      <div className="pilot-page__shell" style={shellStyle}>
        <div className="pilot-page__hero" style={heroStyle}>
          <button
            onClick={() => navigate('/dashboard')}
            style={{
              ...buttonStyle,
              marginBottom: '0.8rem',
              background: 'rgba(182,234,255,0.14)',
              color: '#d9f4ff',
              border: '1px solid rgba(182,234,255,0.22)',
              boxShadow: 'none',
            }}
          >
            BACK TO MAIN PAGE
          </button>

          <div style={{
            display: 'flex',
            justifyContent: 'space-between',
            gap: '1rem',
            flexWrap: 'wrap',
            alignItems: 'flex-end',
          }}>
            <div>
             
              <h2 style={{
                color: '#ffffff',
                fontWeight: 900,
                fontSize: 'clamp(1.6rem, 5vw, 2.2rem)',
                lineHeight: 0.95,
                margin: 0,
              }}>
                CLOSE FLIGHT
              </h2>
              <p style={{
                ...mutedTextStyle,
                maxWidth: 620,
                margin: '0.9rem 0 0',
                fontSize: '1rem',
              }}>
                Complete the remaining actions to close your flight.
              </p>
            </div>

            
          </div>
        </div>

        {!!stripeBanner && (
          <div style={{
            marginBottom: '1rem',
            padding: '0.85rem 1rem',
            borderRadius: 14,
            background: '#0f3d2e',
            color: '#c8ffe5',
            border: '1px solid #2c7f63'
          }}>
            {stripeBanner}
          </div>
        )}

        {loading ? (
          <div style={cardStyle}>Loading...</div>
        ) : error ? (
          <div style={{ ...cardStyle, color: '#ff8d8d' }}>{error}</div>
                ) : bookings.length === 0 ? (
          <div style={{ ...cardStyle, color: '#b6eaff' }}>
            You have no bookings.
          </div>
        ) : postFlightBookings.length === 0 ? (
          <div style={{ ...cardStyle, color: '#b6eaff' }}>
            <div style={{ fontWeight: 800, marginBottom: 6 }}>
              ✓ No outstanding post-flight actions
            </div>
            <div style={{ color: '#9fb7d7' }}>
              All Tech Logs and Payments are complete.
            </div>
          </div>
        ) : (
          <ul style={{ listStyle: 'none', padding: 0, margin: 0, display: 'grid', gap: '1rem' }}>
            
            {postFlightBookings.map(b => {
              const bookingTechEntry = techLogMap[String(b.id)] || {};
              
              const ac = aircraftMap[b.aircraft_id];
              const startDate = getBookingStartDate(b);
              const endDate = getBookingEndDate(b);
              const now = new Date();
              const briefingCutoff = new Date(now.getTime() + 48 * 60 * 60 * 1000);
              const hasFlightTakenPlace = endDate ? endDate <= new Date() : false;
              const isInsideBriefingWindow = startDate ? startDate <= briefingCutoff : false;
              const canOpenLogs = isOwner || (endDate ? endDate <= new Date() : false);
              const canOpenPayment = endDate ? endDate <= new Date() : false;
              const briefingRecord = getBriefingRecordForBooking(b, user?.id);
              const briefingStatus = b.briefing_status || b.briefing_data?.status || getBriefingStatus(briefingRecord);
              const briefingMeta = hasFlightTakenPlace
                ? null
                : !isInsideBriefingWindow
                  ? {
                    label: 'NOT REQUIRED YET',
                    fill: '#9fdcff',
                    border: '#4d79a8',
                    background: 'rgba(77, 121, 168, 0.16)',
                  }
                  : (briefingStatusMeta[briefingStatus] || briefingStatusMeta.red);
              const returnLegMetaBase = getReturnLegStatusMeta(b);
              const returnLegMeta = returnLegMetaBase
                ? (hasFlightTakenPlace
                  ? {
                    label: 'NOT REQUIRED',
                    fill: '#9fdcff',
                    border: '#4d79a8',
                    background: 'rgba(77, 121, 168, 0.16)',
                  }
                  : (!isInsideBriefingWindow
                    ? {
                      label: 'NOT REQUIRED YET',
                      fill: '#9fdcff',
                      border: '#4d79a8',
                      background: 'rgba(77, 121, 168, 0.16)',
                    }
                    : returnLegMetaBase))
                : null;
              const returnLegUpdatedAt = b?.briefing_data?.return_leg_preflight?.updatedAt || b?.briefing_updated_at;
              const returnLegUpdatedLabel = formatStatusUpdatedAt(returnLegUpdatedAt);
              const techStatus = getTechLogStatus(b, bookingTechEntry);
              const paymentStatus = getPaymentStatus(b);
              const paymentTotals = getPaymentTotals(b);
              const stripeConnect = appSettings?.payment?.stripeConnect || {};
              const stripeConnected = !!stripeConnect.onboardingComplete
                && !!stripeConnect.chargesEnabled
                && !!stripeConnect.payoutsEnabled;
              const canPayWithStripe = isMemberStripePaymentEligible({
                booking: b,
                userId: user?.id,
                paymentStatus,
                total: paymentTotals.total,
                paymentEnabled: appSettings.sections.paymentEnabled,
                stripeConnected,
              });
              
              const techPill = getSmallStatusPill(
  techStatus === 'completed'
    ? '✓ TECH LOG COMPLETE'
    : techStatus === 'did_not_fly'
      ? '✓ DID NOT FLY'
    : techStatus === 'defect_reported'
      ? '⚠ DEFECT REPORTED'
      : '⚠ TECH LOG DUE',
  techStatus === 'completed' || techStatus === 'did_not_fly' ? 'good' : 'warning'
);
              const paymentPill = getSmallStatusPill(
  paymentStatus === 'paid'
    ? '✓ PAYMENT COMPLETE'
    : paymentStatus === 'not_required'
      ? '✓ NO PAYMENT REQUIRED'
      : '⚠ PAYMENT DUE',
  paymentStatus === 'paid' || paymentStatus === 'not_required' ? 'good' : 'warning'
);

              return (
                <li className="pilot-booking-card" key={b.id} style={cardStyle}>
                  <div className="pilot-booking-card__header" style={{
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  gap: '1rem',
  marginBottom: '0.75rem',
  flexWrap: 'wrap',
}}>
  <div>
    <div style={{
  color: '#ffffff',
  fontSize: '1.55rem',
  fontWeight: 950,
  letterSpacing: '-0.04em',
  lineHeight: 1,
  marginBottom: 6,
}}>
  {ac ? ac.registration : b.aircraft_id}
</div>

<div style={{
  color: '#8db7e6',
  fontSize: '0.9rem',
  fontWeight: 750,
  letterSpacing: '0.04em',
  textTransform: 'uppercase',
  marginBottom: 8,
}}>
  {ac?.icao_type || 'Aircraft'}
</div>
  </div>

  
</div>
                  

                  {isMultiDayBooking(b) && (
                    <div style={{
                      marginBottom: '1rem',
                      color: '#b6eaff',
                      background: 'rgba(30,207,255,0.08)',
                      border: '1px solid rgba(30,207,255,0.14)',
                      borderRadius: 14,
                      padding: '0.7rem 0.8rem',
                      fontSize: '0.9rem',
                    }}>
                      {formatBookingRange(b)}
                    </div>
                  )}

                  <div className="pilot-booking-card__status" style={{
                    display: 'flex',
                    gap: 8,
                    flexWrap: 'wrap',
                    alignItems: 'center',
                    marginBottom: '0.95rem',
                  }}>
                    {hasFlightTakenPlace && (
                      <>
                        <StatusPill meta={techPill} />
                        <StatusPill meta={paymentPill} />
                      </>
                    )}

                    {briefingMeta ? (
                      <StatusPill meta={briefingMeta} label={`BRIEFING: ${briefingMeta.label}`} />
                    ) : null}

                    {returnLegMeta ? (
                      <StatusPill meta={returnLegMeta} label={`RETURN LEG: ${returnLegMeta.label}`} />
                    ) : null}
                  </div>

                  {!!returnLegUpdatedLabel && returnLegMeta && (
                    <div style={{ color: '#8db7e6', fontSize: '0.78rem', marginTop: '-0.55rem', marginBottom: '0.85rem' }}>
                      Return leg updated: {returnLegUpdatedLabel}
                    </div>
                  )}

                  {canPayWithStripe && (
                    <div className="pilot-booking-card__payment" style={{
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'space-between',
                      gap: '0.8rem',
                      flexWrap: 'wrap',
                      marginBottom: '0.95rem',
                      padding: '0.7rem 0.8rem',
                      borderRadius: 12,
                      background: 'rgba(8,22,38,0.42)',
                      border: '1px solid rgba(182,234,255,0.12)',
                    }}>
                      <div>
                        <div style={{ color: '#8db7e6', fontSize: '0.72rem', fontWeight: 850, letterSpacing: '0.08em' }}>
                          PAYMENT
                        </div>
                        <div style={{ color: '#ffcc66', fontWeight: 850 }}>
                          Outstanding · £{paymentTotals.total.toFixed(2)}
                        </div>
                      </div>

                      <button
                        type="button"
                        onClick={() => handleStripeCheckout(b, paymentTotals)}
                        disabled={checkoutLoadingMap[b.id]}
                        style={{
                          ...buttonStyle,
                          background: '#635bff',
                          color: '#ffffff',
                          opacity: checkoutLoadingMap[b.id] ? 0.65 : 1,
                          cursor: checkoutLoadingMap[b.id] ? 'not-allowed' : 'pointer',
                        }}
                      >
                        {checkoutLoadingMap[b.id] ? 'REDIRECTING...' : 'PAY WITH STRIPE'}
                      </button>
                    </div>
                  )}

                  {!!paymentErrorMap[b.id] && (
                    <div style={{ marginBottom: '0.95rem', color: '#ff8d8d', fontSize: '0.92rem' }}>
                      {paymentErrorMap[b.id]}
                    </div>
                  )}

                  
                  
                  

                  {canOpenLogs ? (
  <button
    className="pilot-booking-card__primary-action"
    type="button"
    onClick={() =>
      navigate(
        `/post-flight?bookingId=${encodeURIComponent(
          String(b.id)
        )}`
      )
    }
    style={{
      ...buttonStyle,
      background: '#b6eaff',
      color: '#181a2a',
      borderRadius: 14,
      padding: '0.45rem 1rem',
      marginTop: 2,
    }}
  >
    Close Flight
  </button>
) : (
                    <div style={{
                      color: '#8db7e6',
                      fontSize: '0.92rem',
                      background: 'rgba(8,22,38,0.34)',
                      border: '1px solid rgba(182,234,255,0.10)',
                      borderRadius: 14,
                      padding: '0.75rem 0.85rem',
                    }}>
                      Flight logs and payment become available on the day of flight.
                    </div>
                  )}

                  {canOpenLogs && expandedBookingId === b.id && (
                    <div style={{
                      marginTop: '1rem',
                      borderTop: '1px solid rgba(182,234,255,0.14)',
                      paddingTop: '1rem'
                    }}>
                      {appSettings.sections.techLogEnabled && (
                        <>
                          {techStatus === 'completed' ? (
  <div
  style={{
    background: 'rgba(15,61,46,0.34)',
    border: '1px solid rgba(125,255,184,0.38)',
    borderRadius: 12,
    padding: '0.7rem 0.85rem',
    color: '#c8ffe5',
    marginBottom: '1rem',
  }}
>
  <div style={{ fontWeight: 800, fontSize: '0.95rem', marginBottom: 10 }}>
  ✓ Tech Log Complete
</div>

<div style={{ color: '#d7fff0', fontSize: '0.88rem', lineHeight: 1.6 }}>
  <div><strong>Flight Time:</strong> {bookingTechEntry?.flightTime || '-'} h</div>
  <div><strong>Method:</strong> {(bookingTechEntry?.recordingMethod || 'hobbs').toUpperCase()}</div>
  <div><strong>Fuel Added:</strong> {bookingTechEntry?.fuelUpliftLitres || 0} L</div>
  <div><strong>Oil Added:</strong> {bookingTechEntry?.oilUpliftLitres || 0} L</div>
  <div><strong>Status:</strong> {(bookingTechEntry?.aircraftStatus || 'serviceable').replace('_', ' ')}</div>
</div>
</div>
) : (
  <AircraftTechLog
    prefill={{
      ...sanitizeTechLogPrefill(bookingTechEntry),
      bookingId: String(b.id),
      flightDate: b.date || '',
      bookingBaseAirfield: baseAirfield || '',
      bookingDestinationAirfield: b?.briefing_data?.destinationAirfield || '',
      bookingReturnAirfield: b?.briefing_data?.returnAirfield || '',
      bookingStartTime: b.time || '',
      bookingEndTime: endDate ? formatLocalTime(endDate) : '',
      pilotName: user?.user_metadata?.full_name || user?.email || '',
    }}
    options={{
      ...appSettings.techLog,
      recordingMethod: ac?.recording_method || appSettings?.techLog?.recordingMethod || 'hobbs',
      checkIntervalHours: appSettings.maintenance?.checkIntervalHours,
      showOptionalFields: false,
    }}
    onSave={entry => handleTechLogSave(b.id, entry)}
  />
)}

                          {!!saveNoticeMap[`${b.id}:tech`] && (
                            <div style={{
                              marginTop: '-1.25rem',
                              marginBottom: '1rem',
                              color: saveNoticeMap[`${b.id}:tech`].toLowerCase().includes('saved') ? '#c8ffe5' : '#ffdfb8',
                              background: saveNoticeMap[`${b.id}:tech`].toLowerCase().includes('saved') ? '#0f3d2e' : '#43260f',
                              border: saveNoticeMap[`${b.id}:tech`].toLowerCase().includes('saved') ? '1px solid #2c7f63' : '1px solid #9a5b2f',
                              borderRadius: 10,
                              padding: '0.55rem 0.75rem'
                            }}>
                              {saveNoticeMap[`${b.id}:tech`]}
                            </div>
                          )}
                        </>
                      )}

                      {appSettings.sections.paymentEnabled && canOpenPayment && (
                        <div style={{
                          background: 'rgba(8,22,38,0.48)',
                          border: '1px solid rgba(182,234,255,0.12)',
                          borderRadius: 16,
                          padding: '1rem',
                          color: '#d7ebff'
                        }}>
                          <h3 style={{ color: '#1ecfff', marginTop: 0, marginBottom: '0.85rem' }}>PAYMENT</h3>
                          {(() => {
                            const totals = getPaymentTotals(b);
                            const checkoutLoading = !!checkoutLoadingMap[b.id];
                            const paymentError = paymentErrorMap[b.id] || '';
                            const paymentBlocked = totals.total <= 0;
                            const stripeConnect = appSettings?.payment?.stripeConnect || {};
                            const stripeConnected = !!stripeConnect.onboardingComplete
                              && !!stripeConnect.chargesEnabled
                              && !!stripeConnect.payoutsEnabled;
                            const connectBlocked = !stripeConnected;

                            return (
                              <>
                                <div style={{
                                  display: 'grid',
                                  gridTemplateColumns: 'repeat(auto-fit, minmax(135px, 1fr))',
                                  gap: '0.65rem',
                                  marginBottom: '1rem',
                                }}>
                                  <div>
                                    <div style={{ color: '#8db7e6', fontSize: '0.75rem', fontWeight: 800 }}>BASIS</div>
                                    <strong>{appSettings.payment.billingBasis}</strong>
                                  </div>
                                  <div>
                                    <div style={{ color: '#8db7e6', fontSize: '0.75rem', fontWeight: 800 }}>HOURS</div>
                                    <strong>{totals.hours.toFixed(2)}</strong>
                                  </div>
                                  <div>
                                    <div style={{ color: '#8db7e6', fontSize: '0.75rem', fontWeight: 800 }}>RATE</div>
                                    <strong>£{Number(appSettings.payment.hourlyRate || 0).toFixed(2)}</strong>
                                  </div>
                                  <div>
                                    <div style={{ color: '#8db7e6', fontSize: '0.75rem', fontWeight: 800 }}>TOTAL</div>
                                    <strong>£{totals.total.toFixed(2)}</strong>
                                  </div>
                                </div>

                                {appSettings.payment.landingFeeEnabled && (
                                  <div style={{ marginBottom: 10 }}>
                                    Landing fee: <strong>£{totals.landingFee.toFixed(2)}</strong>
                                  </div>
                                )}

                                <div style={{ marginBottom: 10, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                                  <button
                                    type="button"
                                    onClick={() => handleStripeCheckout(b, totals)}
                                    disabled={checkoutLoading || paymentBlocked || connectBlocked}
                                    title={
                                      connectBlocked
                                        ? 'Owner must complete Stripe Connect setup before payments can be taken.'
                                        : paymentBlocked
                                          ? 'Total due must be greater than 0 to pay with Stripe.'
                                          : 'Pay this booking with Stripe'
                                    }
                                    style={{
                                      ...buttonStyle,
                                      background: '#635bff',
                                      color: '#ffffff',
                                      opacity: checkoutLoading || paymentBlocked || connectBlocked ? 0.65 : 1,
                                      cursor: checkoutLoading || paymentBlocked || connectBlocked ? 'not-allowed' : 'pointer'
                                    }}
                                  >
                                    {checkoutLoading ? 'REDIRECTING TO STRIPE...' : 'PAY WITH STRIPE'}
                                  </button>

                                </div>

                                {connectBlocked && (
                                  <div style={{ marginTop: 10, color: '#ffcf8a', fontSize: '0.92rem' }}>
                                    Payments are not live yet for this club. The owner must complete Stripe Connect in Admin.
                                  </div>
                                )}

                                {paymentBlocked && (
                                  <div style={{ marginTop: 10, color: '#ffcf8a', fontSize: '0.92rem' }}>
                                    Payment is unavailable because total due is 0. Enter Tech Log times or change billing settings.
                                  </div>
                                )}

                                {!!paymentError && (
                                  <div style={{ marginTop: 10, color: '#ff8d8d', fontSize: '0.92rem' }}>
                                    {paymentError}
                                  </div>
                                )}
                              </>
                            );
                          })()}
                        </div>
                      )}

                      {appSettings.sections.paymentEnabled && !canOpenPayment && (
                        <div style={{ marginTop: '0.6rem', color: '#8db7e6', fontSize: '0.95rem' }}>
                          Payment becomes available on or after the flight date.
                        </div>
                      )}
                    </div>
                  )}
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
}